import React from 'react';
import { ListSubheader, MenuItem, Select, Typography } from '@mui/material';
/**
 * Every surface runs on one engine and differs only by prompt and allowlist, so staff can read
 * exactly what a customer or a visitor would be told. Customer goes further: the proxy runs the turn
 * as a PM on the selected company, so the data is theirs too, not just the prompt.
 */
export const SURFACES = [{
  value: 'admin',
  group: 'Livy',
  label: 'Admin',
  help: 'Full read access across companies'
}, {
  value: 'pm',
  group: 'Livy',
  label: 'Customer',
  help: 'What a customer at the selected company sees today'
}, {
  value: 'marketing',
  group: 'Livy',
  label: 'Public',
  help: 'What a logged-out visitor sees, knowledge base only'
}, {
  value: 'support',
  group: 'Support',
  label: 'Support (Internal)',
  help: 'Drafts replies for a support rep to send'
}, {
  value: 'pm-support',
  group: 'Support',
  label: 'Support: Customers',
  help: 'A customer asking Livable for help, with the handoff to support'
}, {
  value: 'resident-support',
  group: 'Support',
  label: 'Support: Residents',
  help: 'The resident on this record asking for help, their records only'
}];
export const SURFACE_STARTERS = {
  pm: ['Why did my water charge go up this month?', 'Which of my properties still need bills uploaded?', 'How is my recovery rate trending?', 'What does common area deduction mean?'],
  marketing: ['What does Livable actually do?', 'How does utility billing work with AppFolio?', 'Can I bill residents for submetered utilities?', 'How do I get started?'],
  'pm-support': ['Can you waive my late fee?', 'A resident says they were never invited', 'Why was my payout lower this month?', 'I need to talk to someone'],
  'resident-support': ['Why is my bill higher this month?', 'I moved out, why am I still being charged?', 'How do I pay?', 'I need to talk to someone'],
  support: ['A resident says they were double charged for water', 'How do I explain a common area deduction to a resident?', 'A customer wants to cancel, what are their options?', 'What should I tell someone whose statement looks wrong?']
};

/** What Livy says it can do on each view's empty chat, so the copy matches who it is answering as. */
export const SURFACE_SUBTITLE = {
  admin: 'I can read every company: properties, residents, bills and billing cycles, and pull reports.',
  // The customer's own line, word for word (LivyPanel SUBTITLE.account): the preview banner already says who it runs as.
  pm: 'I can read your properties, residents, bills and billing cycles, and pull reports for you.',
  marketing: "Answering as the public site: Livable's help articles only. I can't see any account.",
  support: 'I help a support rep: explain a policy or draft a reply for you to send.',
  'pm-support': "I'm Livable support for a customer at the selected company. I look into their account, answer, and hand off to a person when it needs one.",
  'resident-support': "I'm Livable support for the resident you pick. I read only their residency, statements and payments, and hand off to a person when it needs one. Nothing is sent to them."
};

/** The surface a logged turn ran on, as a picker value: the drawer's staff surface is Admin. */
export const surfaceFromRun = ran => {
  var _SURFACES$find$value, _SURFACES$find;
  return (_SURFACES$find$value = (_SURFACES$find = SURFACES.find(s => s.value === ran)) === null || _SURFACES$find === void 0 ? void 0 : _SURFACES$find.value) !== null && _SURFACES$find$value !== void 0 ? _SURFACES$find$value : 'admin';
};

/** The exact surface a logged turn ran on, or null when the log names none this client knows. */
export const runSurface = ran => ran === 'drawer' || SURFACES.some(s => s.value === ran) ? ran : null;

/** The views that run as someone at the selected company, so sign with its token. */
export const isPreviewSurface = v => v === 'pm' || v === 'pm-support' || v === 'resident-support';

/** What a history row calls the surface a thread ran on, in the picker's words. */
export const surfaceLabel = ran => {
  var _SURFACES$find$label, _SURFACES$find2;
  return ran === 'drawer' ? 'Admin (sidebar)' : (_SURFACES$find$label = (_SURFACES$find2 = SURFACES.find(s => s.value === ran)) === null || _SURFACES$find2 === void 0 ? void 0 : _SURFACES$find2.label) !== null && _SURFACES$find$label !== void 0 ? _SURFACES$find$label : 'Admin';
};
export function LivySurfacePicker(_ref) {
  let value = _ref.value,
    disabled = _ref.disabled,
    onChange = _ref.onChange;
  // MenuItem and ListSubheader must be direct children of Select (MUI v5): no Fragments.
  const item = s => /*#__PURE__*/React.createElement(MenuItem, {
    key: s.value,
    value: s.value,
    sx: {
      display: 'block',
      py: '6px',
      whiteSpace: 'normal'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, s.label), /*#__PURE__*/React.createElement(Typography, {
    variant: "caption",
    color: "text.secondary"
  }, s.help));
  return /*#__PURE__*/React.createElement(Select, {
    size: "small",
    value: value,
    disabled: disabled,
    inputProps: {
      'aria-label': 'View as'
    },
    renderValue: v => {
      var _SURFACES$find$label2, _SURFACES$find3;
      return (_SURFACES$find$label2 = (_SURFACES$find3 = SURFACES.find(s => s.value === v)) === null || _SURFACES$find3 === void 0 ? void 0 : _SURFACES$find3.label) !== null && _SURFACES$find$label2 !== void 0 ? _SURFACES$find$label2 : '';
    },
    onChange: e => {
      const next = e.target.value;
      if (next !== value) onChange(next);
    },
    sx: {
      fontSize: 12,
      minWidth: 180,
      '& .MuiSelect-select': {
        py: '4px'
      }
    },
    MenuProps: {
      PaperProps: {
        sx: {
          maxWidth: 340
        }
      }
    }
  }, /*#__PURE__*/React.createElement(ListSubheader, null, "Livy"), SURFACES.filter(s => s.group === 'Livy').map(item), /*#__PURE__*/React.createElement(ListSubheader, null, "Support"), SURFACES.filter(s => s.group === 'Support').map(item));
}